import { Modal } from '../Common/Modal';
import { Loader } from '../Common/Loader';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import FooterSkeleton from './FooterSkeleton';
import type { BootData } from '@/types/boot';
import { useGetBoot } from '@/hooks/useGetBoot';
import { peekBootConfigSync } from '@/utils/boot';
import { useIsMobile } from '@/hooks/useIsMobile';
import { FeatureFlag } from '@/constants/featureFlags';
import { isFirstTimeDepositor, isDrawerSupportedPaymentMethod } from '@/constants/lastPaymentMethod';

/**
 * Boot-time skeleton for `PreferredPaymentView` (FTD + last-payment-method
 * branches). Plain white background — we deliberately skip the publisher
 * hero image at skeleton time: it shifts in late, can fail to load on
 * boot, and adds an image fetch to the critical path. The real view paints
 * the BG once the rest of the data is ready.
 *
 *   ┌──────────────────────────┐
 *   │   profile + name + items-│
 *   │   toggle row             │
 *   │   total row              │
 *   ├──────────────────────────┤  divider
 *   │   wallet CTA placeholder │
 *   │   2-line EULA placeholder│
 *   ├──────────────────────────┤  footer divider
 *   │   powered-by + 3 links   │
 *   └──────────────────────────┘
 */
/**
 * Skeleton body without the `<Modal>` chrome. Exported so `PreferredPaymentView`
 * can reuse the EXACT same markup as a full-screen overlay while it waits for
 * the native wallet to confirm — keeping the boot skeleton → wallet-gate
 * skeleton hand-off pixel-identical.
 */
export const PreferredPaymentSkeletonContent = () => {
  return (
    <div data-testid="preferred-payment-method-skeleton" className="flex h-full w-full flex-col bg-white">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col pt-3">
          {/* X-button row: kept blank to reserve the same 20px slot the
                real `<XButton />` uses (`relative h-5 w-full`) — without
                this the header rides 20px higher than the live view. */}
          <div className="h-5 w-full" />
          <div className="mt-2 px-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <SkeletonLoader width="w-11" height="h-11" />
                <SkeletonLoader width="w-24" height="h-5" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonLoader width="w-12" height="h-3" />
                <SkeletonLoader width="w-3" height="h-3" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <SkeletonLoader width="w-14" height="h-5" />
              <SkeletonLoader width="w-14" height="h-5" />
            </div>
          </div>
        </div>

        <div className="w-full mt-3 border-t border-base-border" />

        <div className="flex w-full flex-col gap-5 pb-5 pt-6 px-4">
          <SkeletonLoader height="h-12" />
          <div className="flex flex-col items-center gap-2">
            <SkeletonLoader width="w-65" height="h-3" />
            <SkeletonLoader width="w-65" height="h-3" />
          </div>
        </div>
      </div>
      <FooterSkeleton />
    </div>
  );
};

/**
 * Predicts (from boot alone, before checkout data resolves) whether the live
 * view will present the native-wallet bottom sheet, so the boot loader can
 * open as a centered spinner over a dim backdrop (matching the sheet's dim
 * chrome) instead of the full-screen modal — no "covers the whole screen
 * first" flash. FTD → device wallet or the nominated local APM; last-method →
 * the BE-supplied method limited to the drawer scope
 * (apple/google/paypal/card/blik/blik_checkout/pix). A rare miss (wallet turns
 * out unavailable → DEFAULT) just falls back to the modal a frame later, which
 * is acceptable.
 *
 * Accepts the verified boot when present, else the synchronous unverified
 * {@link peekBootConfigSync} hint — so the sheet-shape loader is chosen on the
 * FIRST render (pre-verify) and never flashes the full-screen modal.
 */
const willPresentDrawer = (
  hint: Pick<BootData, 'checkoutConfiguration' | 'featureFlags'> | null | undefined,
  isMobile: boolean
): boolean => {
  if (!isMobile) return false;
  if (!hint?.featureFlags?.[FeatureFlag.CHECKOUT_DRAWER_FOR_LAST_METHOD]) return false;
  const cfg = hint?.checkoutConfiguration;

  // FTD → device wallet or the nominated local APM, both sheet-presented.
  if (isFirstTimeDepositor(cfg)) return true;

  /*
   * Returning depositor whose boot names no method at all. The method is
   * arriving on `/checkout/v3` — the source the last-method branch reads first
   * (see `PreferredPaymentTemplate.lastUsedPaymentMethod`) — and that request
   * has not resolved yet, so there is nothing here to test.
   *
   * Predict the sheet anyway. The drawer scope covers every method PREFERRED
   * can render except LINK and AMAZON_PAY, so it is the overwhelmingly likely
   * shape, and the two mistakes are not symmetric: guessing the modal shows a
   * full white screen that visibly collapses into a small sheet, while
   * guessing the sheet shows a neutral centered spinner over the same dim the
   * modal already uses.
   */
  if (!cfg?.preferredPaymentMethod) return true;

  // A named method still decides on its own merits: the drawer scope is
  // narrower than PREFERRED's eligibility (`SUPPORTED_LAST_PAYMENT_METHODS`),
  // so LINK / AMAZON_PAY render inside PREFERRED via the legacy modal. A rare
  // miss (method absent from BE paymentMethods, or CARD without a saved card —
  // guards only the template can evaluate) falls back to the modal a frame later.
  return isDrawerSupportedPaymentMethod(cfg.preferredPaymentMethod);
};

/**
 * Boot loader for the swipe-up (drawer) presentation: a clean centered spinner
 * over a dim backdrop. NOT a sheet — the sheet's single slide-up entrance is
 * owned by the live view (see `CheckoutBottomSheet` + `PreferredPaymentView`)
 * so the shopper sees exactly one smooth iOS-style slide when the app is ready
 * to render, instead of the sheet opening under a spinner and then swapping.
 *
 * The dim backdrop is identical to the pre-React `#boot-skeleton` loader
 * (index.html) and to the sheet's own backdrop, so the three loading frames
 * share the same dim and the hand-off is invisible.
 */
const PreferredPaymentDrawerLoader = () => (
  <div
    data-testid="preferred-payment-drawer-loader"
    aria-hidden="true"
    role="presentation"
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
  >
    <Loader size="lg" variant="checkout" />
  </div>
);

const PreferredPaymentSkeleton = () => {
  const { boot } = useGetBoot();
  const isMobile = useIsMobile();

  if (willPresentDrawer(boot ?? peekBootConfigSync(), isMobile)) {
    return <PreferredPaymentDrawerLoader />;
  }

  return (
    <Modal>
      <PreferredPaymentSkeletonContent />
    </Modal>
  );
};

export default PreferredPaymentSkeleton;
